import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ModuleFlow, handleError } from '../../../../shared/utils/error.utils';
import { buildHttpParams } from '../../../../shared/utils/http.utils';
import { ApiResponse } from '../../../../shared/dto/response/api-response.dto';
import { getApiBaseUrl } from '../../../../shared/config/runtime-config';
import { GuestRepository } from '../../domain/repository';
import {
  GuestFindAllRequest,
  GuestFindAllResponse,
  GuestFindRequest,
  GuestFindResponse,
  GuestRemoveRequest,
  GuestRemoveResponse,
  GuestSaveRequest,
  GuestSaveResponse,
} from '../../domain/dto';
import { Guest } from '../../domain/model';

interface GuestApiListResponse {
  items: Array<Record<string, unknown>>;
  page: number;
  pageSize: number;
  totalCount: number;
}

@Injectable({ providedIn: 'root' })
export class GuestRepositoryImpl implements GuestRepository {
  constructor(private http: HttpClient) {}

  findAllPath(uploadId: number): string {
    return `${this.apiUrl}/${uploadId}/guests`;
  }

  findAll(payload: GuestFindAllRequest): Observable<HttpResponse<GuestFindAllResponse>> {
    const { uploadId, ...query } = payload;
    const params = buildHttpParams(query);

    return this.http
      .get<ApiResponse<GuestApiListResponse>>(this.findAllPath(uploadId), {
        observe: 'response',
        params,
      })
      .pipe(
        map((response) => {
          const data = response.body?.data;
          const mappedBody: GuestFindAllResponse = {
            items: (data?.items ?? []).map((item) => this.mapApiGuest(item)),
            page: data?.page ?? 1,
            pageSize: data?.pageSize ?? 50,
            totalCount: data?.totalCount ?? 0,
          };
          return response.clone({ body: mappedBody });
        }),
        catchError((error) => handleError(ModuleFlow.GuestFindAll, error)),
      );
  }

  find(payload: GuestFindRequest): Observable<HttpResponse<GuestFindResponse>> {
    return this.http
      .get<ApiResponse<Record<string, unknown>>>(
        `${this.findAllPath(payload.uploadId)}/${payload.guestId}`,
        { observe: 'response' },
      )
      .pipe(
        map((response) => response.clone({ body: this.mapApiGuest(response.body?.data) })),
        catchError((error) => handleError(ModuleFlow.GuestFind, error)),
      );
  }

  save(payload: GuestSaveRequest): Observable<HttpResponse<GuestSaveResponse>> {
    return this.http
      .put<ApiResponse<GuestSaveResponse>>(
        `${this.findAllPath(payload.uploadId)}/${payload.id}`,
        {
          idRing: payload.idRing,
          name: payload.name,
          lastName: payload.lastName,
          entity: payload.entity,
          position: payload.position,
        },
        { observe: 'response' },
      )
      .pipe(
        map((response) => response.clone({ body: response.body?.data })),
        catchError((error) => handleError(ModuleFlow.GuestSave, error)),
      );
  }

  remove(payload: GuestRemoveRequest): Observable<HttpResponse<GuestRemoveResponse>> {
    return this.http
      .delete<ApiResponse<GuestRemoveResponse>>(`${this.findAllPath(payload.uploadId)}/${payload.id}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response.clone({ body: response.body?.data })),
        catchError((error) => handleError(ModuleFlow.GuestRemove, error)),
      );
  }

  private mapApiGuest(item: unknown): Guest {
    const row = this.toRecord(item);
    const identification =
      this.toNullableString(row['identification']) ??
      this.toNullableString(row['fullIdentification']);

    return {
      id: this.toNullableNumber(row['id']),
      idUploadedFile: this.toNullableNumber(row['idUploadedFile']),
      identification,
      name: this.toNullableString(row['name']),
      lastName: this.toNullableString(row['lastName']),
      entity: this.toNullableString(row['entity']),
      position: this.toNullableString(row['position']),
      nationalityTypeName: this.toNullableString(row['nationalityTypeName']),
      nationalityTypeCode: this.toNullableString(row['nationalityTypeCode']),
      idNationalityType: this.toNullableNumber(row['idNationalityType']),
      ringName: this.toNullableString(row['ringName']),
      ringCode: this.toNullableString(row['ringCode']),
      idRing: this.toNullableNumber(row['idRing']),
      idColor: this.toNullableNumber(row['idColor']),
      colorName: this.toNullableString(row['colorName']),
      colorCode: this.toNullableString(row['colorCode']),
      isEditable: this.toNullableBoolean(row['isEditable']),
      isDeletable: this.toNullableBoolean(row['isDeletable']),
      condition: this.toNullableString(row['condition']),
      created: this.toNullableString(row['created']),
      updated: this.toNullableString(row['updated']),
    };
  }

  private toNullableNumber(value: unknown): number | null {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toNullableString(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = String(value).trim();
    return parsed.length > 0 ? parsed : null;
  }

  private toNullableBoolean(value: unknown): boolean | null {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true') return true;
      if (normalized === 'false') return false;
    }

    return null;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }

  private get apiUrl(): string {
    return getApiBaseUrl('uploaded-files');
  }
}


